import { FC, useMemo, useCallback } from 'react'
import { PopupRelationId } from 'interfaces/peergroups'
import { SidebarScrollablePopupPlace } from 'components/features/pfm/SidebarPopupPlace/SidebarScrollablePopupPlace'
import { PeergroupMenuPopupContainer } from 'components/features/pfm/PeergroupMenuPopup/PeergroupMenuPopupContainer'
import { PeergroupMenuMemberPopupContainer } from 'components/features/pfm/PeergroupMenuPopup/PeergroupMenuMemberPopupContainer'
import { peergroupPopupSelector } from 'selectors/globalSelectors'
import { useShallowSelector, useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { usePopupPlacePosition } from 'helpers/hookHelpers'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { hidePeergroupPopupActionCreator } from 'actions/sidebarActions'

type PeergroupPopupPlaceProps = {}

export const PeergroupPopupPlace: FC<PeergroupPopupPlaceProps> = () => {
  const { relationId, boundingRect: targetRect } = useShallowSelector((state) => ({
    relationId: null,
    boundingRect: null,
    ...peergroupPopupSelector(state),
  }))

  const viewportSegment = useStoreSelector(viewportSegmentSelector)
  const position = usePopupPlacePosition()
  const dispatch = useStoreDispatch()
  const onHidePopup = useCallback<(popupRelationId: PopupRelationId) => void>((popupRelationId) => {
    if (!popupRelationId) {
      return
    }

    dispatch(hidePeergroupPopupActionCreator(popupRelationId))
  }, [dispatch])

  const popupChild = useMemo(() => {
    if (relationId === null) {
      return null
    }

    const corners = position === 'fill' ? 'none' : 'all'
    const onBackButtonClick = position === 'fill'
      ? () => onHidePopup(relationId)
      : undefined

    if (relationId === 'member') {
      return (
        <PeergroupMenuMemberPopupContainer
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    return (
      <PeergroupMenuPopupContainer
        corners={ corners }
        relationId={ relationId }
        onBackButtonClick={ onBackButtonClick }
      />
    )
  }, [position, onHidePopup, relationId])

  if (!targetRect || !popupChild) {
    return null
  }

  return (
    <SidebarScrollablePopupPlace
      targetRect={ targetRect }
      position={ position }
      alwaysShowScrollbar={ viewportSegment !== 'desktop' }
    >
      { popupChild }
    </SidebarScrollablePopupPlace>
  )
}
